/**
 * When is a tour step's `path` already satisfied? — the demo tour's route
 * matcher, kept pure (no DOM, no router) so it can be tested directly.
 *
 * It used to be a PREFIX check: "already anywhere under the path counts".
 * Every route in a demo starts with `/w/demo-<subdomain>`, so a step whose
 * path was the workspace home never moved a prospect off the landing canvas
 * (`/w/demo-x/c/…` starts with `/w/demo-x`), and the bare string prefix also
 * matched the secondary desks (`/w/demo-x--finance/…`). Each such step then
 * hunted for its target on the wrong page and fell back to a centered card.
 *
 * The rule now is the one the path tokens already used: the prospect is on
 * the step's page only when the PATHNAME IS the step's route — trailing
 * slash, query and fragment ignored. No step needs more than that: every
 * page a tour opens carries its own anchors (the canvases sheet lives on
 * `/w/<slug>`, not on the canvas under it; the datasets table on
 * `/manage/datasets`, not on a dataset), so being somewhere below a route is
 * never being on it.
 */

/** A route as the tour compares it: the pathname only — no query, no
 * fragment, no trailing slash ("/" stays "/"). */
export function routePathname(path: string): string {
  const pathname = (path.split(/[?#]/, 1)[0] ?? "").trim();
  return pathname.replace(/\/+$/, "") || (pathname.startsWith("/") ? "/" : "");
}

/** Whether a prospect standing on `currentPathname` is already on the page
 * `stepPath` opens: exact pathname equality, so a step navigates unless the
 * current route IS its route. An empty step path names no page. */
export function isOnStepPath(currentPathname: string, stepPath: string): boolean {
  const target = routePathname(stepPath);
  return target !== "" && routePathname(currentPathname) === target;
}
